import React from 'react';
import { Layout } from 'antd';
import styled from 'styled-components';
import { Project } from '../../store/projectSlice';
import { ProjectStage } from '../../constants/stages';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/userSlice';
import logo from "../../assets/logo.png";
import UserInfo from './UserInfo';
import ProjectSelector from '../project/ProjectSelector';
import StageProgress from '../project/StageProgress';

const { Sider } = Layout;

const StyledSider = styled(Sider)`
  background: #fff !important;
  padding: 16px;
  position: relative;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
  }
  
  .ant-layout-sider-trigger {
    display: none;
  }
  
  @media (max-width: 768px) {
    position: fixed;
    height: 100vh;
    z-index: 1000;
    left: 0;
    top: 64px;
    padding: 16px;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease-in-out;
    
    .ant-layout-sider-trigger {
      display: block;
      background: #fff !important;
      border-right: 1px solid #f0f0f0;
      color: #595959;
      width: 100% !important;
      
      &:hover {
        color: #1890ff;
      }
    }
    
    &.ant-layout-sider-collapsed {
      padding: 16px 0;
      transform: translateX(-100%);
      box-shadow: none;
      
      .ant-layout-sider-trigger {
        width: 0 !important;
      }
    }
  }
`;

const SiderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-bottom: 100px;
  overflow: hidden;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  
  img {
    height: 32px;
    width: auto;
  }
  
  span {
    font-size: 1.2rem;
    font-weight: bold;
    color: #1890ff;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

interface ProjectSidebarProps {
  projects: Project[];
  currentProject: Project | null;
  selectedStage: ProjectStage;
  collapsed: boolean;
  onProjectSelect: (project: Project) => void;
  onStageSelect: (stage: ProjectStage) => void;
  onCreateClick: () => void;
  onEditClick: () => void;
  onCollapse: (collapsed: boolean) => void;
}

const ProjectSidebar: React.FC<ProjectSidebarProps> = ({
  projects,
  currentProject,
  selectedStage,
  collapsed,
  onProjectSelect,
  onStageSelect,
  onCreateClick,
  onEditClick,
  onCollapse
}) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <StyledSider
      width={250}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
    >
      <LogoContainer onClick={handleLogoClick}>
        <img src={logo} alt="来创业 Logo" />
        <span>来创业</span>
      </LogoContainer>
      <SiderContent>
        <ProjectSelector
          projects={projects}
          currentProject={currentProject}
          onProjectSelect={onProjectSelect}
          onCreateClick={onCreateClick}
          onEditClick={onEditClick}
        />
        <StageProgress
          currentStage={currentProject?.currentStage || ProjectStage.PROBLEM_DEFINITION}
          selectedStage={selectedStage}
          onStageSelect={onStageSelect}
        />
      </SiderContent>
      <UserInfo user={user ? {
        email: user.email,
        isPremium: !!user.isPremium
      } : undefined} />
    </StyledSider>
  );
};

export default ProjectSidebar; 